import { Component, ReactNode } from 'react';
import { Box, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { TextMDSemiBold, SMRegular } from '@/typography/Typography';
import { useLocation } from 'react-router-dom';

interface Props {
    children: ReactNode;
    tabName?: string;
}

interface State {
    hasError: boolean;
    errorMessage: string;
}

export class TabErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, errorMessage: '' };
    }

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            errorMessage: error?.message ?? 'An unexpected error occurred.'
        };
    }

    componentDidCatch(error: Error) {
        console.error(
            `[TabErrorBoundary] Error in tab "${
                this.props.tabName ?? 'unknown'
            }":`,
            error
        );
    }

    handleReset = () => {
        this.setState({ hasError: false, errorMessage: '' });
    };

    render() {
        if (this.state.hasError) {
            return (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={2}
                    p={6}
                    sx={{
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: 'error.light',
                        backgroundColor: '#fff5f5',
                        mt: 2
                    }}
                >
                    <ErrorOutlineIcon color="error" sx={{ fontSize: 48 }} />
                    <TextMDSemiBold style={{ color: '#c62828' }}>
                        Something went wrong
                        {this.props.tabName
                            ? ` in "${this.props.tabName}"`
                            : ''}
                    </TextMDSemiBold>
                    <SMRegular
                        color="text.secondary"
                        style={{ textAlign: 'center', maxWidth: 480 }}
                    >
                        {this.state.errorMessage}
                    </SMRegular>
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={this.handleReset}
                        sx={{ mt: 1 }}
                    >
                        Try again
                    </Button>
                </Box>
            );
        }

        return this.props.children;
    }
}

/**
 * Functional wrapper that resets the error boundary whenever the route changes.
 * Use this instead of TabErrorBoundary directly.
 */
export const TabErrorBoundaryWrapper = ({
    children,
    tabName
}: {
    children: ReactNode;
    tabName?: string;
}) => {
    const { pathname } = useLocation();
    // key changes on every navigation → remounts the class boundary → clears error state
    return (
        <TabErrorBoundary key={pathname} tabName={tabName}>
            {children}
        </TabErrorBoundary>
    );
};
